import { useGetGettingStartedEventsSummaryQuery } from '../../../store/metrics/metrics'
import { useMemo } from 'react'
import { OnboardingTrackEvent } from '../../../apps/professional-app/fa/common/tracking'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetMeQuery } from '../../../store/user/user'
import { getInviteRoleGroups } from '../../ManageTeam/InviteRoleGroups'
import { RoleType } from '../../../models/Role'
import { getIntegrationServiceIdsForCategory, IntegrationCategory, IntegrationServiceId } from '../../../common/integrations'

export const useGettingStarted = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: gettingStartedEvents, refetch, isSuccess } = useGetGettingStartedEventsSummaryQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const { data: userData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const enabledByRole = useMemo(() => {
    const sidecarRoleGroup = getInviteRoleGroups().find((r) => r.type === RoleType.SIDECAR)
    if (userData && sidecarRoleGroup) {
      return userData.roles.every((r) => !sidecarRoleGroup.roleIds.includes(r.id))
    }
    return false
  }, [userData])

  const finishedSteps = useMemo(() => {
    return gettingStartedEvents?.data.map((e) => e.eventName)
  }, [gettingStartedEvents])
  const isPresetsFinished = useMemo(() => {
    return (
      finishedSteps?.includes(OnboardingTrackEvent.ALL_DISMISSED) ||
      finishedSteps?.includes(OnboardingTrackEvent.ONBOARDING_PRESETS_DISMISSED) ||
      finishedSteps?.includes(OnboardingTrackEvent.ONBOARDING_PRESETS_DONE)
    )
  }, [finishedSteps])
  const isZoomIntegrationFinished = useMemo(() => {
    return (
      (userData && userData.integrations?.find((i) => i.serviceId === IntegrationServiceId.ZOOM)) ||
      finishedSteps?.includes(OnboardingTrackEvent.ALL_DISMISSED) ||
      finishedSteps?.includes(OnboardingTrackEvent.ZOOM_CONNECTED) ||
      finishedSteps?.includes(OnboardingTrackEvent.ONBOARDING_ZOOM_INTEGRATION_DISMISSED)
    )
  }, [finishedSteps, userData])
  const isCalendarIntegrationFinished = useMemo(() => {
    return (
      (userData &&
        userData.integrations?.some((i) => getIntegrationServiceIdsForCategory(IntegrationCategory.CALENDAR).includes(i.serviceId as IntegrationServiceId))) ||
      finishedSteps?.includes(OnboardingTrackEvent.ALL_DISMISSED) ||
      finishedSteps?.includes(OnboardingTrackEvent.CALENDAR_CONNECTED) ||
      finishedSteps?.includes(OnboardingTrackEvent.ONBOARDING_CALENDAR_INTEGRATION_DISMISSED) ||
      false
    )
  }, [finishedSteps, userData])
  const isDemoMeetingViewedFinished = useMemo(() => {
    return (
      finishedSteps?.includes(OnboardingTrackEvent.ALL_DISMISSED) ||
      finishedSteps?.includes(OnboardingTrackEvent.ONBOARDING_DEMO_MEETING_VIEWED_DISMISSED) ||
      finishedSteps?.includes(OnboardingTrackEvent.DEMO_MEETING_VIEWED)
    )
  }, [finishedSteps])
  const isFinishedGettingStarted = useMemo(() => {
    if (isSuccess && finishedSteps && finishedSteps.length > 0) {
      return (
        finishedSteps.includes(OnboardingTrackEvent.ALL_DISMISSED) ||
        [isPresetsFinished, isZoomIntegrationFinished, isCalendarIntegrationFinished, isDemoMeetingViewedFinished].every((e) => e)
      )
    }
    return false
  }, [finishedSteps, isSuccess, isPresetsFinished, isZoomIntegrationFinished, isCalendarIntegrationFinished, isDemoMeetingViewedFinished])

  return {
    onboardingEnabled: enabledByRole && isSuccess && !isFinishedGettingStarted,
    gettingStartedEvents,
    finishedSteps,
    isFinishedGettingStarted,
    isPresetsFinished,
    isZoomIntegrationFinished,
    isCalendarIntegrationFinished,
    isDemoMeetingViewedFinished,
    refetchEvents: refetch,
    gettingStartedEventsLoaded: isSuccess,
  }
}
